import { 
  useGetGoogleAdsTemplatesQuery,
  useCreateGoogleAdsTemplateMutation,
  useUpdateGoogleAdsTemplateMutation,
  useRemoveGoogleAdsTemplateMutation,
  useCreateGoogleAdsRecordMutation,
  useUpdateGoogleAdsTemplateAssigneeMutation  // 新增這行
} from "../../../../generated/graphql";
import CreateMarkupButton from "../../markupSetting/CreateMarkupButton";
import AccountSelect from "../google/GoogleAccountSelect";
import CampaignSelect from "../google/CampaignSelect";
import { AdsPlatformAdapter, ReportListItem, MetricsGroup, GoogleTemplateExtra } from "./types";
import { ReportList } from "../ReportList";
import React from "react";
import { Grid } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { styled } from "@material-ui/styles";
import { StyledToggleButton } from "../../../../components/StyledToggleButton";

const AiMaxScopeGroup = styled(ToggleButtonGroup)({
  backgroundColor: '#fff',
  border: '1px solid #d0d0d0',
  borderRadius: 6,
  overflow: 'hidden',
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: 'none',
    borderRadius: 0,
  },
  '& .MuiToggleButton-root': {
    width: 'auto',
    padding: '6px 16px',
    textTransform: 'none',
    fontWeight: 400,
    color: '#666',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#fff',
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: '#fff',
      backgroundColor: '#C81F4D',
    },
  },
  '& .MuiToggleButton-root:not(:last-child)': {
    borderRight: '1px solid #d0d0d0',
  },
});


const reportList: ReportListItem[] = ReportList.map(r => ({
  name: r.name,
  title: r.title,
  fields: r.fields,
  badge: r.badge,
  note: r.note,
  noteWhenAiMaxOnly: r.noteWhenAiMaxOnly,
}));

const metricsGroups: MetricsGroup[] = [
  { 
    title: "基本", 
    fields: [
      { field: "clicks", label: "點擊" },
      { field: "impressions", label: "曝光" },
      { field: "ctr", label: "點閱率" },
      { field: "average_cpc", label: "平均單次點擊出價" },
      { field: "cost_micros", label: "花費" },
      { field: "average_cpm", label: "平均千次曝光出價" },
    ]
  },
  { 
    title: "轉換", 
    fields: [
      { field: "conversions", label: "轉換" },
      { field: "cost_per_conversion", label: "單次轉換費用" },
      { field: "conversions_from_interactions_rate", label: "轉換率" },
      { field: "conversions_value", label: "總轉換價值" },
      { field: "value_per_conversion", label: "單次轉換價值" },
      { field: "roas", label: "轉換價值/費用" },
      { field: "all_conversions", label: "所有轉換" },
      { field: "all_conversions_value", label: "所有轉換價值" },
      { field: "all_conversions_from_interactions_rate", label: "所有轉換率" },
      { field: "cost_per_all_conversions", label: "單次所有轉換費用" },
      { field: "view_through_conversions", label: "瀏覽後轉換" },
      { field: "new_customer_lifetime_value", label: "新顧客" },
    ]
  },
  { 
    title: "曝光比重", 
    fields: [
      { field: "search_impression_share", label: "搜尋曝光比重" },
      { field: "search_top_impression_share", label: "搜尋頂端曝光比重" },
      { field: "search_absolute_top_impression_share", label: "搜尋絕對頂端曝光比重" },
      { field: "top_impression_percentage", label: "曝光率(頂端)" },
      { field: "absolute_top_impression_percentage", label: "曝光率(絕對頂端)" },
    ]
  },
  { 
    title: "影音", 
    fields: [
      { field: "video_trueview_views", label: "觀看數" },
      { field: "video_trueview_view_rate", label: "收視率" },
      { field: "trueview_average_cpv", label: "平均單次收視出價" },
      { field: "video_quartile_p25_rate", label: "影片播放進度 25%" },
      { field: "video_quartile_p50_rate", label: "影片播放進度 50%" },
      { field: "video_quartile_p75_rate", label: "影片播放進度 75%" },
      { field: "video_quartile_p100_rate", label: "影片播放進度 100%" },
    ]
  },
  { 
    title: "GA4", 
    fields: [
      { field: "average_page_views", label: "事件/工作階段" },
      { field: "average_time_on_site", label: "每個工作階段的平均參與時間(秒)" },
    ]
  },
  { 
    title: "互動參與", 
    fields: [
      { field: "interactions", label: "互動" },
      { field: "interaction_rate", label: "互動率" },
      { field: "engagements", label: "參與" },
      { field: "engagement_rate", label: "參與度" },
      { field: "average_cpe", label: "平均單次參與出價" },
    ]
  },
];


export const AdTypes = [
  { key: "search", label: "搜尋" },
  { key: "display", label: "多媒體" },
  { key: "shopping", label: "購物" },
  { key: "video", label: "影音" },
  { key: "performance_max", label: "最高成效" },
  { key: "demand_gen", label: "需求開發" },
  { key: "app", label: "應用程式" },
];

const AI_MAX_SCOPE_OPTIONS: { key: GoogleTemplateExtra['aiMaxScope']; label: string; hint: string; pool: string }[] = [
  {
    key: 'ALL',
    label: '全部（含 AI Max）',
    hint: '預設。搜尋活動全集（含已啟用 AI Max 者）進入數據池，為與 Google Ads 後台對帳之基準。',
    pool: '搜尋（全部，含 AI Max）',
  },
  {
    key: 'ONLY',
    label: '僅 AI Max 活動',
    hint: '僅 enable_ai_max = TRUE 之搜尋活動進入數據池（依產表當下設定實時判定）。注意：期間中途切換設定之活動，整段期間依當下狀態歸屬。',
    pool: '搜尋（僅 AI Max）',
  },
  {
    key: 'EXCLUDE',
    label: '排除 AI Max 活動',
    hint: '排除已啟用 AI Max 之搜尋活動，作為對照組，供評估 AI Max 開啟前後成效差異。',
    pool: '搜尋（排除 AI Max）',
  },
];

export function normalizeGoogleExtra(value: any): GoogleTemplateExtra {
  if (Array.isArray(value)) {
    return { adTypes: value, aiMaxScope: 'ALL' };
  }
  const scope = value?.aiMaxScope;
  return {
    adTypes: value?.adTypes ?? [],
    aiMaxScope: scope === 'ONLY' || scope === 'EXCLUDE' ? scope : 'ALL',
  };
}

export function getGoogleExtraFromTemplate(template?: any): GoogleTemplateExtra {
  return {
    adTypes: template?.adTypes ?? [],
    aiMaxScope: template?.aiMaxScope === 'ONLY' || template?.aiMaxScope === 'EXCLUDE' ? template.aiMaxScope : 'ALL',
  };
}

const GoogleExtraOptions: React.FC<{ value?: any; onChange: (next: GoogleTemplateExtra) => void }> =
({ value, onChange }) => {
  const extra = normalizeGoogleExtra(value);
  const searchSelected = extra.adTypes.includes('search');
  const scopeOption = AI_MAX_SCOPE_OPTIONS.find(option => option.key === extra.aiMaxScope) || AI_MAX_SCOPE_OPTIONS[0];

  const toggleAdType = (key: string) => {
    const selected = extra.adTypes.includes(key);
    const nextTypes = selected ? extra.adTypes.filter(k => k !== key) : [...extra.adTypes, key];
    const nextScope = nextTypes.includes('search') ? extra.aiMaxScope : 'ALL';
    onChange({ adTypes: nextTypes, aiMaxScope: nextScope });
  };

  return (
    <Grid container spacing={1} direction="column">
      <Grid item>
        <Grid container spacing={1}>
          {AdTypes.map(({ key, label }) => (
            <Grid item key={key}>
              <StyledToggleButton
                selected={extra.adTypes.includes(key)}
                value="check"
                style={{ width: 'auto' }}
                onChange={() => toggleAdType(key)}
              >
                {label}
              </StyledToggleButton>
            </Grid>
          ))}
        </Grid>
      </Grid>
      {searchSelected ? (
        <Grid item>
          <div style={{
            marginTop: 4,
            padding: '12px 16px 14px',
            background: '#f3f3f3',
            border: '1px solid #e6e6e6',
            borderRadius: 8,
            borderLeft: '3px solid #C81F4D',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 14, marginRight: 4 }}>AI Max 範圍</span>
              <AiMaxScopeGroup
                exclusive
                size="small"
                value={extra.aiMaxScope}
                onChange={(_event, next: GoogleTemplateExtra['aiMaxScope'] | null) => {
                  if (!next) return;
                  onChange({ ...extra, aiMaxScope: next });
                }}
              >
                {AI_MAX_SCOPE_OPTIONS.map(option => (
                  <ToggleButton key={option.key} value={option.key}>
                    {option.label}
                  </ToggleButton>
                ))}
              </AiMaxScopeGroup>
            </div>
            <div style={{ marginTop: 10, fontSize: 13, color: '#757575', lineHeight: 1.6 }}>
              {scopeOption.hint}
            </div>
            <div style={{
              marginTop: 6,
              fontSize: 13,
              lineHeight: 1.8,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <span style={{ color: '#333' }}>本模板數據池：</span>
              <span style={{
                display: 'inline-block',
                margin: '0 8px',
                background: '#fdecef',
                color: '#C81F4D',
                border: '1px solid #C81F4D',
                borderRadius: 4,
                padding: '3px 10px',
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.4,
              }}>
                {scopeOption.pool}
              </span>
              <span style={{ color: '#757575' }}>
                各分頁維持既有設計，直接加總所有已勾選廣告類型；AI Max 範圍僅過濾搜尋活動集合
              </span>
            </div>
          </div>
        </Grid>
      ) : null}
    </Grid>
  );
};

const FieldMap = {
  customer: "帳戶",
  campaign: "廣告活動",
  ad_group: "廣告群組",
  ad_group_ad: "廣告",
  keyword: "關鍵字",
  ai_max_broad_match: "AI Max 擴充比對",
  ai_max_landing_page: "AI Max 相符的到達網頁",
  gender: "性別",
  age_range: "年齡層",
  device: "裝置",
  ad_schedule: "時段",
  label: "標籤",
  conversion_action: "轉換動作",
  all_conversion_action: "所有轉換動作",
  audience: "目標對象",
  location: "指定地區",
  geographic: "地理位置",
  user_location: "使用者地區",
  asset: '額外資訊',
  asset_group: '素材資源群組',

  total: "總覽",
  daily: "每日",
  daily_total: "每日總計",

  clicks: "點擊",
  impressions: "曝光",
  ctr: "點閱率",
  average_cpc: "平均單次點擊出價",
  cost_micros: "花費",
  interactions: "互動",
  interaction_rate: "互動率",
  top_impression_percentage: "曝光率(頂端)",
  absolute_top_impression_percentage: "曝光率(絕對頂端)",
  engagements: "參與",
  engagement_rate: "參與度",
  average_cpe: "平均單次參與出價",
  average_cpm: "平均千次曝光出價",

  conversions: "轉換",
  cost_per_conversion: "單次轉換費用",
  conversions_from_interactions_rate: "轉換率",
  conversions_value: "總轉換價值",
  value_per_conversion: "單次轉換價值",
  roas: "轉換價值/費用",
  all_conversions: "所有轉換",
  all_conversions_value: "所有轉換價值",
  all_conversions_from_interactions_rate: "所有轉換率",
  cost_per_all_conversions: "單次所有轉換費用",
  view_through_conversions: "瀏覽後轉換",
  new_customer_lifetime_value: "新顧客",

  search_impression_share: "搜尋曝光比重",
  search_top_impression_share: "搜尋頂端曝光比重",
  search_absolute_top_impression_share: "搜尋絕對頂端曝光比重",

  video_trueview_views:"觀看數",
  video_trueview_view_rate:"收視率",
  trueview_average_cpv:"平均單次收視出價",
  // backward compatibility labels
  video_views:"觀看數",
  video_view_rate:"收視率",
  average_cpv:"平均單次收視出價",
  video_quartile_p25_rate:"影片播放進度 25%",
  video_quartile_p50_rate:"影片播放進度 50%",
  video_quartile_p75_rate:"影片播放進度 75%",
  video_quartile_p100_rate:"影片播放進度 100%",

  // engagement_rate: "互動工作階段百分比",
  average_page_views: "事件/工作階段",
  average_time_on_site: "每個工作階段的平均參與時間(秒)",

  'no_match':"(無比對)",
  'no_match-total':"總覽(無比對)",
  'no_match-daily':"每日(無比對)",
  'search_term': "搜尋字詞",
  'customer-daily':"帳戶(每日)",
  'campaign-daily':"廣告活動每日",
  'ad_group-daily':"廣告群組每日",
  ad_group_ad_label:"廣告標籤",
  ad_group_ad_keyword:"廣告關鍵字",
  campaign_district:"廣告活動(縣/市)",
  campaign_city:"廣告活動(城市)",
  campaign_region:"廣告活動(指定地區)",

  "county": '縣/市',
  "city": '城市',
  "region": '指定地區',
}



export const googleAdapter: AdsPlatformAdapter = {
  key: 'google',
  displayName: 'Google Ads',
  nameLabel: '報表名稱',
  tableNameHeader: 'Google Ads 報表名稱',
  createButtonText: '新增Google Ads報表設定',
  generateQueryEntity: 'googleAds',
  removeUnexposedLabel: '移除所有未曝光資料',

  formatField: (k) => FieldMap[k] ?? k,
  reportList,
  metricsGroups,
  extraOptionsRender: GoogleExtraOptions,
  getExtraFromTemplate: getGoogleExtraFromTemplate,

  AccountSelect: AccountSelect,
  CampaignSelect: CampaignSelect,
  MarkupButton: () => <CreateMarkupButton platform="google" />,

  useGetTemplatesQuery: () => {
    const { data, loading, error } = useGetGoogleAdsTemplatesQuery();
    return { data: data ? { templates: data?.googleAdsTemplates! } : undefined, loading, error };
  },
  useCreateTemplateMutation: () => {
    const [mutate] = useCreateGoogleAdsTemplateMutation();
    return [mutate];
  },
  useUpdateTemplateMutation: () => {
    const [mutate] = useUpdateGoogleAdsTemplateMutation();
    return [mutate];
  },
  useRemoveTemplateMutation: () => {
    const [mutate] = useRemoveGoogleAdsTemplateMutation();
    return [mutate];
  },
  useCreateRecordMutation: () => {
    const [mutate] = useCreateGoogleAdsRecordMutation();
    return [mutate];
  },
  useUpdateTemplateAssigneeMutation: () => {
    const [mutate] = useUpdateGoogleAdsTemplateAssigneeMutation();
    return [mutate];
  },

  refetchTemplatesQueryName: "getGoogleAdsTemplates",
  refetchRecordListQueryName: "getGoogleAdsRecordList",
  recordInputKey: "googleAdsRecordInput",
  templateInputKey: "googleAdsTemplateInput",

  buildTemplateInput: ({ name, reportNames, metricsFields, accountCampaigns, assigneeId, removeUnexposed, extra }) => {
    const googleExtra = normalizeGoogleExtra(extra);
    return {
      name, 
      reportNames, 
      metricsFields, 
      accountCampaigns, 
      assigneeId, 
      removeUnexposed,
      adTypes: googleExtra.adTypes,
      aiMaxScope: googleExtra.aiMaxScope,
    };
  },
};
